import React, { useState, useEffect } from 'react';
import styles from '../../_css/Pricing.module.css';
import { getPricingUsageReports, getAgencyList, postPricingSystemConfiguration, getPricingSystemConfiguration, getPricingStatements, downloadPricingStatement } from '../../requests';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  PageHead,
  SectionTitle,
  Panel,
  ActionsBar,
  Select,
  Btn,
  ToggleSwitch,
} from './shared';
import { useDispatch } from 'react-redux';
import { startLoader, stopLoader } from '../../_store/reducers/loaderReducer';
import { notifyError, notifySuccess } from '../../utils/helper';
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];


const USAGE_PERIOD_START = { month: 5, year: 2026 };

const formatPeriodLabel = (year, month) => `${MONTH_NAMES[month - 1]} ${year}`;

const getCurrentPeriodLabel = () => {
  const now = new Date();
  return formatPeriodLabel(now.getFullYear(), now.getMonth() + 1);
};

const getPeriodOptions = () => {
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth() + 1;
  const options = [];

  while (year > USAGE_PERIOD_START.year || (year === USAGE_PERIOD_START.year && month >= USAGE_PERIOD_START.month)) {
    options.push(formatPeriodLabel(year, month));
    month -= 1;
    if (month < 1) {
      month = 12;
      year -= 1;
    }
  }

  return options;
};

const NOTIFY_ORDERING_USER_KEY = 'notify_ordering_user_agency_admin';
const NOTIFY_TOPUP_ORDER_KEY = 'notify_topup_order';

const parseConfigBoolean = (value, defaultValue = true) => {
  if (value == null || value === '') return defaultValue;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
  if (normalized === 'false' || normalized === '0' || normalized === 'no') return false;
  return defaultValue;
};

const toConfigBooleanString = (value) => (value ? 'true' : 'false');

export default function UsageSection() {
  const [consumptionRows, setConsumptionRows] = useState([]);
  const [statementRows, setStatementRows] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [tierFilter, setTierFilter] = useState('');
  const [termFilter, setTermFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState(getCurrentPeriodLabel);
  const [agencyFilter, setAgencyFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [showStatementPreview, setShowStatementPreview] = useState(false);
  const [notifyUser, setNotifyUser] = useState();
  const [notifyTopup, setNotifyTopup] = useState();
  const [orderRecipients, setOrderRecipients] = useState('');
  const [orderRecipientsCc, setOrderRecipientsCc] = useState('');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [totalPages, setTotalPages] = useState(1);

  const dispatch = useDispatch();

  useEffect(() => {
    const loadAgencies = async () => {
      try {
        const res = await getAgencyList();
        const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.data) ? res.data.data : [];
        setAgencies(
          rows.map((a) => {
            const id = a?.id ?? a?.agency?.id ?? a?.agency_id ?? a?.agencyId;
            const name = a?.name ?? a?.agency?.name ?? a?.agency_name ?? a?.label ?? `Agency ${id}`;
            return { value: String(id ?? ''), label: String(name) };
          }),
        );
      } catch (err) {
        // ignore
      }
    };
    loadAgencies();
  }, []);

  useEffect(() => {
    const loadNotificationSettings = async () => {
      try {
        const [recipientsCfg, recipientsCcCfg, notifyUserCfg, notifyTopupCfg] = await Promise.all([
          getPricingSystemConfiguration({ key: 'order_recipients' }),
          getPricingSystemConfiguration({ key: 'order_recipients_cc' }),
          getPricingSystemConfiguration({ key: NOTIFY_ORDERING_USER_KEY }),
          getPricingSystemConfiguration({ key: NOTIFY_TOPUP_ORDER_KEY }),
        ]);

        if (recipientsCfg?.data?.value != null) {
          setOrderRecipients(recipientsCfg.data.value);
        }
        if (recipientsCcCfg?.data?.value != null) {
          setOrderRecipientsCc(recipientsCcCfg.data.value);
        }
        if (notifyUserCfg?.data?.value != null) {
          setNotifyUser(parseConfigBoolean(notifyUserCfg.data.value));
        }
        if (notifyTopupCfg?.data?.value != null) {
          setNotifyTopup(parseConfigBoolean(notifyTopupCfg.data.value));
        }
      } catch (err) {
        // keep defaults
      }
    };
    loadNotificationSettings();
  }, []);

  const parsePeriod = (display) => {
    const months = {
      January: 1,
      February: 2,
      March: 3,
      April: 4,
      May: 5,
      June: 6,
      July: 7,
      August: 8,
      September: 9,
      October: 10,
      November: 11,
      December: 12,
    };
    if (!display) return {};
    const parts = String(display).split(' ');
    if (parts.length === 2 && months[parts[0]]) {
      return { month: months[parts[0]], year: Number(parts[1]) };
    }
    return {};
  };

  const normalizeConsumption = (item) => {
    const tenant = item?.tenent?.name ?? item?.tenant ?? item?.customer ?? item?.customer_name ?? (item?.agency?.name) ?? '';
    const agencyId = item?.agency?.id ?? item?.agency_id ?? item?.tenent?.id ?? null;
    const tier = item?.tier ?? item?.plan_tier ?? '';
    const term = item?.term ?? item?.term ?? item?.billing_cycle ?? '';
    const used = Number(item?.used ?? item?.credits_used ?? item?.used_credits ?? 0) || 0;
    const included = Number(item?.included ?? item?.included_credits ?? item?.includedCredits ?? 0) || 0;
    const topups = Number(item?.top_ups ?? item?.topups_cr ?? 0) || 0;
    const revenue = typeof item?.revenue === 'number' ? `€${item.revenue}` : item?.revenue ?? '';
    return { tenant, agencyId, tier, term, used, included, topups, revenue };
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const p = parsePeriod(periodFilter);
        const params = { page, limit };
        if (p.month) params.month = p.month;
        if (p.year) params.year = p.year;
        if (tierFilter) params.tier = tierFilter;
        if (termFilter) params.term = termFilter;
        if (agencyFilter) params.agency_id = Number(agencyFilter);

        const [consumptionRes, statementsRes] = await Promise.all([
          getPricingUsageReports(params),
          getPricingStatements(params),
        ]);

        let consumption = [];
        let nextTotalPages = 1;
        if (consumptionRes && consumptionRes.data) {
          const payload = consumptionRes.data;
          const d = payload.data || payload;

          if (Array.isArray(d)) {
            consumption = d.map(normalizeConsumption);
          } else if (d && Array.isArray(d.consumption)) {
            consumption = d.consumption.map(normalizeConsumption);
          }

          // parse pagination
          const pag = payload.pagination || payload.meta || payload;
          if (pag) {
            const total = pag.total ?? pag.totalItems ?? pag.totalCount ?? pag.count ?? null;
            const perPage = pag.perPage ?? pag.per_page ?? pag.limit ?? limit;
            if (total != null && perPage) {
              nextTotalPages = Math.max(1, Math.ceil(Number(total) / Number(perPage)));
            } else if (pag.totalPages) {
              nextTotalPages = pag.totalPages;
            }
          }
        }
        setConsumptionRows(consumption);
        setTotalPages(nextTotalPages);

        let statements = [];
        if (statementsRes && statementsRes.data) {
          const payload = statementsRes.data;
          const d = payload.data || payload;

          if (d && Array.isArray(d.statements)) {
            statements = d.statements;
          }
          else if (d && Array.isArray(d)) {
            statements = d;
          }
        }
        setStatementRows(statements);
      } catch (e) {
        console.error(e);
        setConsumptionRows([]);
        setStatementRows([]);
        setTotalPages(1);
        notifyError(e?.response?.data?.message || 'Failed to load usage & statement data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tierFilter, termFilter, periodFilter, agencyFilter, page, limit]);

  const display = (v) => {
    if (v == null) return '';
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (typeof v === 'object') return v.name || v.label || (v.id ? String(v.id) : JSON.stringify(v));
    return String(v);
  };

  const toggleStatement = () => {
    setShowStatementPreview((prev) => !prev);
  };

  const saveOrderRecipients = async () => {
    dispatch(startLoader())
    try {
      const res = await postPricingSystemConfiguration({ key: 'order_recipients', value: orderRecipients });
      if (res && res.data) {
        notifySuccess(res.data.message);
      } else {
        notifyError(res.message || 'Failed to save order recipients');
      }
    } catch (e) {
      notifyError(e.message || 'Failed to save order recipients');
    }
    dispatch(stopLoader())
  };

  const saveOrderRecipientsCc = async () => {
    dispatch(startLoader())
    try {
      const res = await postPricingSystemConfiguration({ key: 'order_recipients_cc', value: orderRecipientsCc });
      if (res && res.data) {
        notifySuccess(res.data.message);
      } else {
        notifyError(res.message || 'Failed to save order recipients (cc)');
      }
    } catch (e) {
      notifyError(e.message || 'Failed to save order recipients (cc)');
    }
    dispatch(stopLoader())
  };

  const saveNotificationSetting = async (key, nextValue, revert) => {
    dispatch(startLoader());
    try {
      const res = await postPricingSystemConfiguration({ key, value: toConfigBooleanString(nextValue) });
      if (res?.data) {
        notifySuccess(res.data.message || 'Setting saved');
      } else {
        notifyError(res?.message || 'Failed to save setting');
        revert();
      }
    } catch (e) {
      notifyError(e?.message || 'Failed to save setting');
      revert();
    }
    dispatch(stopLoader());
  };

  const handleNotifyUserToggle = () => {
    const next = !notifyUser;
    setNotifyUser(next);
    saveNotificationSetting(NOTIFY_ORDERING_USER_KEY, next, () => setNotifyUser(!next));
  };

  const handleNotifyTopupToggle = () => {
    const next = !notifyTopup;
    setNotifyTopup(next);
    saveNotificationSetting(NOTIFY_TOPUP_ORDER_KEY, next, () => setNotifyTopup(!next));
  };

  const downloadStatement = async (statement) => {
    dispatch(startLoader())
    try {
      const agencyId = statement?.agency_id;
      const month = statement?.period?.month;
      const year = statement?.period?.year;

      if (!agencyId || !month || !year) {
        notifyError('Missing statement details for download');
        return;
      }

      const response = await downloadPricingStatement(agencyId, { month, year });
      if (response?.status < 400 && response?.data instanceof Blob) {

        const blob = response.data;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `statement-${agencyId}-${year}-${month}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        notifyError('Failed to generate statement PDF');
      }
    } catch (e) {
      console.error(e);
      notifyError(e?.response?.data?.message || e?.message || 'Failed to generate statement PDF');
    } finally {
      // INV-08: also on the early return above, which used to leave the loader running.
      dispatch(stopLoader())
    }
  };

  // INV-08: rows of deleted agencies have no agency (id and name gone); no PDF for them.
  const isNamelessStatement = (row) => !row?.agency_id || !row?.agency_name;

  return (
    <>
      <PageHead title="Usage & statements" subtitle="Consumption and monthly statements" />

      <SectionTitle>Consumption</SectionTitle>
      <Panel>
        <div className={styles.filterBar}>
          <span className={styles.filterLabel}>Filter</span>
          <Select small value={tierFilter || ''} onChange={(v) => { setTierFilter(v); setPage(1); }} options={[{ value: '', label: 'All tiers' }, 'S', 'M', 'L']} />
          <Select small value={termFilter || ''} onChange={(v) => { setTermFilter(v); setPage(1); }} options={[{ value: '', label: 'All terms' }, { value: 'monthly', label: 'Monthly' }, { value: 'yearly', label: 'Yearly' }]} />
          <Select
            small
            value={periodFilter}
            onChange={(v) => { setPeriodFilter(v); setPage(1); }}
            options={getPeriodOptions()}
          />
          <Select
            small
            value={agencyFilter}
            onChange={(v) => { setAgencyFilter(v); setPage(1); }}
            options={[{ value: '', label: 'All agencies' }, ...agencies.map((a) => ({ value: a.value, label: a.label }))]}
          />
        </div>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Agency ID</th>
              <th>Tenant</th>
              <th>Tier</th>
              <th>Term</th>
              <th className={styles.alignRight}>Used / incl.</th>
              <th className={styles.alignRight}>Top-ups cr</th>
              <th className={styles.alignRight}>Period revenue</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className={styles.muted}>
                  Loading…
                </td>
              </tr>
            ) :  consumptionRows.length > 0 ? consumptionRows.map((row, idx) => (
              <tr key={display(row.tenant) || idx}>
                <td className={styles.muted}>
                  {row.agencyId ? `MPSA-${row.agencyId}` : '—'}
                </td>
                <td>
                  <b>{display(row.tenant)}</b>
                </td>
                <td>{display(row.tier)}</td>
                <td>{display(row.term)}</td>
                <td className={styles.alignRight}>
                  {display(row.used)} / {row.tier === 'U' ? 'Unlimited' : display(row.included)}
                </td>
                <td className={styles.alignRight}>{row.topups ? `+${display(row.topups)}` : 0}</td>
                <td className={styles.alignRight}>{display(row.revenue)}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={7} className={styles.muted}>
                  No data found
                </td>
              </tr>
            )}
            {/* pagination footer removed from table rows */}
          </tbody>
        </table>
        <ActionsBar>
          <span className={styles.actionsSpacer} />
          <Btn ghost sm onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
            <ChevronLeft size={14} />
          </Btn>
          <span className={styles.muted} style={{ margin: '0 8px' }}>
            Page {page} / {totalPages}
          </span>
          <Btn ghost sm onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
            <ChevronRight size={14} />
          </Btn>
        </ActionsBar>
      </Panel>

      <SectionTitle>Monthly statements</SectionTitle>
      <Panel>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Period</th>
              <th className={styles.alignRight}>Billable</th>
              <th>VAT</th>
              <th className={styles.alignRight}>Statement</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className={styles.muted}>
                  Loading…
                </td>
              </tr>
            ) : !statementRows?.length ? (
              <tr>
                <td colSpan={5} className={styles.muted}>
                  No data found
                </td>
              </tr>
            ) : statementRows.map((row) => (
              <tr
                key={`${row.agency_id ?? 'none'}-${row.period?.year}-${row.period?.month}`}
                className={row.preview ? styles.rowLink : undefined}
                onClick={row.preview ? toggleStatement : undefined}
              >
                <td>
                  <b>{display(row.agency_name)}</b>
                </td>
                <td>{display(row.period.month)}/{display(row.period.year)}</td>
                <td className={styles.alignRight}>€{display(row.billable)}</td>
                <td className={styles.muted}>{display(row.vat)}(19%)</td>
                <td className={styles.alignRight}>
                  <Btn
                    ghost
                    sm
                    disabled={isNamelessStatement(row)}
                    title={isNamelessStatement(row) ? 'No PDF: this agency no longer exists' : undefined}
                    onClick={(e) => {
                      if (row.preview) e.stopPropagation();
                      downloadStatement(row);
                    }}
                  >
                    Generate PDF
                  </Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* <ActionsBar>
          <Btn ghost sm>
            Generate all
          </Btn>
        </ActionsBar> */}
      </Panel>

      {showStatementPreview && (
        <div className={styles.mini} style={{ marginTop: 18 }}>
          <h4>Statement preview · Mediaplus Germany · June 2026</h4>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Agency</div>
            <span className={styles.readOnly}>Mediaplus GmbH</span>
          </div>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Billing address</div>
            <span className={styles.readOnly}>Brienner Str. 45, 80333 München, DE</span>
          </div>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Tier · contract term</div>
            <span className={styles.readOnly}>L (Large) · Yearly (-10%)</span>
          </div>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Period</div>
            <span className={styles.readOnly}>2026-06-01 → 2026-06-30</span>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Credits used by action</th>
                <th className={styles.alignRight}>Credits</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Full simulation</td>
                <td className={styles.alignRight}>46</td>
              </tr>
              <tr>
                <td>Persona generation</td>
                <td className={styles.alignRight}>12</td>
              </tr>
              <tr>
                <td>Results translation</td>
                <td className={styles.alignRight}>4</td>
              </tr>
              <tr>
                <td>
                  <b>Total used</b>
                </td>
                <td className={styles.alignRight}>
                  <b>62 / 120 included</b>
                </td>
              </tr>
            </tbody>
          </table>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>Top-up orders</div>
            <span className={styles.readOnly}>#2241 · +40 cr · €2.200,00</span>
          </div>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>VAT note</div>
            <span className={styles.readOnly}>19% DE</span>
          </div>
          <div className={styles.setRow}>
            <div className={styles.setRowLabel}>
              <b>Total (net)</b>
            </div>
            <span className={styles.readOnly}>
              <b>€2.200,00</b>
            </span>
          </div>
          <ActionsBar>
            <Btn ghost sm>
              Download PDF
            </Btn>
          </ActionsBar>
        </div>
      )}

      <SectionTitle>Order notifications</SectionTitle>
      <Panel>
        <div className={styles.setRow}>
          <div className={styles.setRowLabel}>
            Order recipients (Mediaplus)
            <small>
              Distribution list notified on each top-up order, for the manual invoice handoff.
            </small>
          </div>
          <div className={styles.inp} style={{ minWidth: 260 }}>
            <input
              value={orderRecipients}
              onChange={(e) => setOrderRecipients(e.target.value)}
              style={{ width: 230 }}
            />
            <Btn sm onClick={saveOrderRecipients}>
              Save
            </Btn>
          </div>
        </div>
        <div className={styles.setRow}>
          <div className={styles.setRowLabel}>
            Order recipients (Mediaplus) cc
            <small>
              Distribution list cc&apos;d on each top-up order, for the manual invoice handoff.
            </small>
          </div>
          <div className={styles.inp} style={{ minWidth: 260 }}>
            <input
              value={orderRecipientsCc}
              onChange={(e) => setOrderRecipientsCc(e.target.value)}
              style={{ width: 230 }}
            />
            <Btn sm onClick={saveOrderRecipientsCc}>
              Save
            </Btn>
          </div>
        </div>
        <div className={styles.setRow}>
          <div className={styles.setRowLabel}>Notify ordering user &amp; agency admin</div>
          <ToggleSwitch on={notifyUser} onToggle={handleNotifyUserToggle} />
        </div>
        <div className={styles.setRow}>
          <div className={styles.setRowLabel}>Notify on top-up order</div>
          <ToggleSwitch on={notifyTopup} onToggle={handleNotifyTopupToggle} />
        </div>
        <div className={styles.setRow}>
          <div className={styles.setRowLabel}>Email delivery</div>
          <span className={styles.readOnly}>EU residency · AWS SES Frankfurt</span>
        </div>
      </Panel>
    </>
  );
}
